import { ReactElement } from 'react';

import { InputFailureSvg, InputSuccessSvg } from '@cashier/cashier-assets';
import {
  Directions,
  PaymentFormField,
  ValidationStatuses,
} from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { cn } from '@cashier/cashier-ui';

import { useFormPayloadItem } from '../../../../../hooks';
import { useSecuredFormStateContext } from '../../../contexts';
import { CommonField } from '../common/cashierField';

import defaultStyles from './defaultStyles.module.scss';
import inlineStyles from './inlineStyles.module.scss';

interface ComponentProps {
  field: PaymentFormField;
  direction: Directions;
}

export const MaskedField = ({
  field,
  direction,
}: ComponentProps): ReactElement => {
  const { placeholder, name, label } = field;
  const inputLabelPosition = useUIConfiguration('input', 'label');
  const isDefaultLabelPosition = inputLabelPosition === 'default';
  const styleModule = isDefaultLabelPosition ? defaultStyles : inlineStyles;

  const { validationStatus } = useFormPayloadItem(name);
  const { fieldLoaded, getPayloadItem } = useSecuredFormStateContext();

  const { isFocused, isFilled } = getPayloadItem(name);

  const isError = validationStatus === ValidationStatuses.FAILED;

  const inputStyles = isDefaultLabelPosition
    ? cn({
        [styleModule.input]: true,
        [styleModule.active]: isFocused,
        [styleModule.error]: isError,
      })
    : cn({
        [styleModule.input]: true,
        [styleModule.active]: isFocused,
        [styleModule.error]: isError,
        [styleModule.rtl]: direction === Directions.Rtl,
      });

  return (
    <div
      className={cn({
        [styleModule.col]: true,
        [styleModule.wrapper]: true,
        [styleModule.rtl]: direction === Directions.Rtl,
      })}
    >
      {isDefaultLabelPosition && (
        <>
          <label
            htmlFor={name}
            className={styleModule.label}
            data-test={`${name}-input-label`}
          >
            {label}
          </label>

          <span
            className={cn({
              [styleModule.placeholder]: true,
              [styleModule.active]: isFocused || isFilled,
              [styleModule.rtl]: direction === Directions.Rtl,
            })}
          >
            {placeholder}
          </span>
        </>
      )}

      <div className={inputStyles}>
        <CommonField
          name={name}
          title={field.label || ''}
          loading="lazy"
          onLoad={() => fieldLoaded(name)}
          labelPosition={inputLabelPosition}
        />
      </div>

      {!isDefaultLabelPosition && (
        <>
          <label
            className={cn({
              [styleModule.label]: true,
              [styleModule.active]: isFocused || isFilled || !!placeholder,
              [styleModule.dynamicLabel]: !placeholder,
              [styleModule.rtl]: direction === Directions.Rtl,
            })}
          >
            {label}
          </label>

          <span
            className={cn({
              [styleModule.placeholder]: true,
              [styleModule.active]: isFocused || isFilled,
              [styleModule.rtl]: direction === Directions.Rtl,
            })}
          >
            {placeholder}
          </span>

          <div
            className={cn({
              [styleModule.icon]: true,
              [styleModule.rtl]: direction === Directions.Rtl,
            })}
          >
            {validationStatus === ValidationStatuses.SUCCESS && (
              <div className={styleModule.success}>
                <InputSuccessSvg />
              </div>
            )}
            {isError && (
              <div className={styleModule.error}>
                <InputFailureSvg />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
